import React, { useMemo } from 'react'

import { Tooltip } from 'slab'
import { conditionallyLocalizeDate } from 'utils'
import type { MembersEnrollmentOverviewFinancialStatus } from 'utils/web_api.schemas'

import { snakeCaseToSentenceCase } from '../../utils'

type FinancialStatusIndicatorProps =
  | {
    status: 'behind_on_payments'
    carrierName: string
    coverageEndDate?: Date
    showCaption?: boolean
  }
  | {
    status: 'inactive'
    carrierName?: string
    coverageEndDate: Date
    showCaption?: boolean
  }
  | {
    status: MembersEnrollmentOverviewFinancialStatus
    carrierName?: string
    coverageEndDate?: Date
    showCaption?: boolean
  }

const FinancialStatusIndicator: React.FC<FinancialStatusIndicatorProps> = ({
  status,
  carrierName,
  coverageEndDate,
  showCaption = true,
}) => {
  const cursorClass = showCaption ? '' : 'cursor-pointer'

  const { circleClass, textClass, caption } = useMemo(() => {
    switch (status) {
      case 'active':
        return {
          circleClass: 'bg-green-300',
          textClass: 'text-green-500',
          caption: 'Everything looks good!'
        }
      case 'pending_payment':
        return {
          circleClass: 'bg-orange-400',
          textClass: 'text-orange-500',
          caption: <>We're working on getting your balance paid up. There's nothing you need to do!</>
        }
      case 'behind_on_payments':
        return {
          circleClass: 'bg-orange-400',
          textClass: 'text-orange-500',
          caption: `Your plan might not be fully paid up. If it's behind, make a payment${carrierName ? ` to ${carrierName}` : ''} as soon as possible to keep your coverage going`
        }
      case 'canceled':
        return {
          circleClass: 'bg-red-400',
          textClass: 'text-red-500',
          caption: <>Your coverage has ended, possibly due to nonpayment</>
        }
      case 'inactive':
        return {
          circleClass: 'bg-gray-500',
          textClass: 'text-gray-500',
          caption: coverageEndDate && `Coverage ended ${conditionallyLocalizeDate(coverageEndDate)}`
        }
      case 'unknown':
        return {
          circleClass: 'bg-white border border-gray-500',
          textClass: 'text-main',
          caption: 'This plan was purchased outside of Thatch'
        }
    }
  }, [status, carrierName, coverageEndDate])

  return (
    <div className='flex flex-col gap-1'>
      <div className='flex flex-nowrap items-center gap-2'>
        <div className={`w-2 h-2 rounded-full ${circleClass}`} />
        <Tooltip
          align='center'
          content={caption}
          side='bottom'
          enabled={!showCaption}
        >
          <div className={`body ${textClass} whitespace-nowrap ${cursorClass}`}>{snakeCaseToSentenceCase(status)}</div>
        </Tooltip>
      </div>
      {showCaption && <div className='caption text-gray-500'>{caption}</div>}
    </div>
  )
}

export default FinancialStatusIndicator
